import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, X, FolderOpen } from "lucide-react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "../../lib/api";

const inputCls = "w-full border border-line bg-ink px-3 py-2.5 text-sm text-white outline-none transition-colors focus:border-accent";

const EMPTY = {
  code: "", name: "", category: "土木工事", customer_id: "", address: "", description: "",
  start_planned: "", end_planned: "", start_actual: "", end_actual: "",
  manager_id: "", staff_ids: [], status: "計画中", contract_amount: 0, budget_amount: 0,
  contract_date: "", contract_type: "請負", order_number: "", payment_terms: "", inspection_date: "", progress_percent: 0, notes: "",
};

const CATEGORIES = ["土木工事", "草刈り・環境整備", "伐採工事", "太陽光発電設備工事", "不動産開発", "その他"];
const STATUSES = ["計画中", "施工中", "完了", "中止"];

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] tracking-[0.3em] text-gray-500">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

export default function AdminProjects() {
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [staff, setStaff] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get("/admin/projects").then((r) => setItems(r.data)).catch(() => toast.error("取得に失敗しました"));
    api.get("/admin/customers").then((r) => setCustomers(r.data)).catch(() => {});
    api.get("/admin/staff").then((r) => setStaff(r.data)).catch(() => {});
  };

  useEffect(() => {
    load();
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...form, contract_amount: Number(form.contract_amount) || 0, budget_amount: Number(form.budget_amount) || 0 };
    try {
      if (editing.id) await api.put(`/admin/projects/${editing.id}`, payload);
      else await api.post("/admin/projects", payload);
      toast.success("保存しました");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`「${p.name}」を削除しますか？`)) return;
    try {
      await api.delete(`/admin/projects/${p.id}`);
      toast.success("削除しました");
      load();
    } catch {
      toast.error("削除に失敗しました");
    }
  };

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const toggleStaff = (id) => setForm((f) => ({ ...f, staff_ids: f.staff_ids.includes(id) ? f.staff_ids.filter((x) => x !== id) : [...f.staff_ids, id] }));

  const customerName = (id) => customers.find((c) => c.id === id)?.name || "—";
  const staffName = (id) => staff.find((s) => s.id === id)?.name || "—";

  return (
    <div data-testid="admin-projects-page">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="font-mincho text-2xl font-bold text-white">工事案件の管理</h1>
          <p className="mt-1 font-mono text-[10px] tracking-[0.3em] text-gray-500">PROJECTS</p>
        </div>
        <button data-testid="project-add-button" onClick={() => { setForm(EMPTY); setEditing({}); }} className="flex items-center gap-2 bg-accent px-5 py-2.5 font-display text-xs font-bold tracking-[0.15em] text-ink transition-colors hover:bg-accent-hover">
          <Plus className="h-4 w-4" />
          案件を登録
        </button>
      </div>

      <div className="mt-8 overflow-x-auto border border-line">
        <table className="w-full min-w-[800px] text-left text-sm">
          <thead>
            <tr className="border-b border-line bg-coal font-mono text-[10px] tracking-[0.2em] text-gray-500">
              <th className="px-4 py-3">案件番号</th>
              <th className="px-4 py-3">工事名称</th>
              <th className="px-4 py-3">発注者</th>
              <th className="px-4 py-3">期間</th>
              <th className="px-4 py-3">担当</th>
              <th className="px-4 py-3">進捗</th>
              <th className="px-4 py-3">状態</th>
              <th className="px-4 py-3">操作</th>
            </tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.id} data-testid={`project-row-${p.id}`} className="border-b border-line bg-panel last:border-b-0">
                <td className="px-4 py-3 font-mono text-xs text-gray-400">{p.code}</td>
                <td className="px-4 py-3 text-xs font-bold text-white">{p.name}</td>
                <td className="px-4 py-3 text-xs text-gray-400">{customerName(p.customer_id)}</td>
                <td className="whitespace-nowrap px-4 py-3 text-xs text-gray-400">{p.start_planned} 〜 {p.end_planned}</td>
                <td className="px-4 py-3 text-xs text-gray-400">{(p.staff_ids || []).length}名</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className="h-1.5 w-16 overflow-hidden rounded-full bg-line">
                      <div className="h-full bg-accent" style={{ width: `${Math.min(100, Math.max(0, p.progress_percent || 0))}%` }} />
                    </div>
                    <span data-testid={`project-progress-${p.id}`} className="font-mono text-[10px] text-gray-400">{p.progress_percent || 0}%</span>
                  </div>
                </td>
                <td className="px-4 py-3 text-xs"><span className={p.status === "施工中" ? "text-accent" : p.status === "完了" ? "text-green-400" : "text-gray-400"}>{p.status}</span></td>
                <td className="px-4 py-3">
                  <div className="flex gap-2">
                    <Link data-testid={`project-detail-${p.id}`} to={`/admin/projects/${p.id}`} className="border border-line p-2 text-gray-400 transition-colors hover:border-accent hover:text-accent" title="まとめ画面">
                      <FolderOpen className="h-3.5 w-3.5" />
                    </Link>
                    <button data-testid={`project-edit-${p.id}`} onClick={() => { setForm({ ...EMPTY, ...p }); setEditing(p); }} className="border border-line p-2 text-gray-400 transition-colors hover:border-accent hover:text-accent">
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button data-testid={`project-delete-${p.id}`} onClick={() => remove(p)} className="border border-line p-2 text-gray-400 transition-colors hover:border-red-500 hover:text-red-400">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-sm text-gray-500">案件が登録されていません。</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/80 p-4 backdrop-blur-sm">
          <form data-testid="project-form" onSubmit={save} className="max-h-[90vh] w-full max-w-2xl space-y-4 overflow-y-auto border border-line bg-coal p-8">
            <div className="flex items-center justify-between">
              <h2 className="font-mincho text-lg font-bold text-white">{editing.id ? "案件を編集" : "案件を登録"}</h2>
              <button type="button" data-testid="project-form-close" onClick={() => setEditing(null)} className="text-gray-500 hover:text-white"><X className="h-5 w-5" /></button>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="工事名称 *"><input data-testid="project-form-name" required value={form.name} onChange={set("name")} className={inputCls} /></Field>
              <Field label="案件番号（空欄で自動採番）"><input value={form.code} onChange={set("code")} className={inputCls} placeholder="P-202610-XXXX" /></Field>
              <Field label="工事種別">
                <select data-testid="project-form-category" value={form.category} onChange={set("category")} className={inputCls}>
                  {CATEGORIES.map((c) => (<option key={c}>{c}</option>))}
                </select>
              </Field>
              <Field label="発注者">
                <select data-testid="project-form-customer" value={form.customer_id} onChange={set("customer_id")} className={inputCls}>
                  <option value="">—</option>
                  {customers.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
                </select>
              </Field>
              <Field label="着工予定日"><input data-testid="project-form-start" type="date" value={form.start_planned} onChange={set("start_planned")} className={inputCls} /></Field>
              <Field label="完成予定日"><input type="date" value={form.end_planned} onChange={set("end_planned")} className={inputCls} /></Field>
              <Field label="実着工日"><input type="date" value={form.start_actual} onChange={set("start_actual")} className={inputCls} /></Field>
              <Field label="実完成日"><input type="date" value={form.end_actual} onChange={set("end_actual")} className={inputCls} /></Field>
              <Field label="契約金額（円）"><input type="number" value={form.contract_amount} onChange={set("contract_amount")} className={inputCls} /></Field>
              <Field label="実行予算（円）"><input type="number" value={form.budget_amount} onChange={set("budget_amount")} className={inputCls} /></Field>
            </div>
            <div className="border border-line bg-ink p-4">
              <p className="mb-3 font-mono text-[10px] tracking-[0.3em] text-gray-500">契約情報</p>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="契約日"><input data-testid="project-form-contract-date" type="date" value={form.contract_date} onChange={set("contract_date")} className={inputCls} /></Field>
                <Field label="契約方式">
                  <select data-testid="project-form-contract-type" value={form.contract_type} onChange={set("contract_type")} className={inputCls}>
                    <option>請負</option><option>準委任</option><option>その他</option>
                  </select>
                </Field>
                <Field label="注文番号"><input data-testid="project-form-order-number" value={form.order_number} onChange={set("order_number")} className={inputCls} /></Field>
                <Field label="完成検査日"><input data-testid="project-form-inspection-date" type="date" value={form.inspection_date} onChange={set("inspection_date")} className={inputCls} /></Field>
              </div>
              <div className="mt-4">
                <Field label="支払条件"><input data-testid="project-form-payment-terms" value={form.payment_terms} onChange={set("payment_terms")} className={inputCls} placeholder="例：完工後30日以内" /></Field>
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="状態">
                <select data-testid="project-form-status" value={form.status} onChange={set("status")} className={inputCls}>
                  {STATUSES.map((s) => (<option key={s}>{s}</option>))}
                </select>
              </Field>
              <Field label="現場責任者">
                <select data-testid="project-form-manager" value={form.manager_id} onChange={set("manager_id")} className={inputCls}>
                  <option value="">—</option>
                  {staff.filter((s) => s.is_active).map((s) => (<option key={s.id} value={s.id}>{s.name}</option>))}
                </select>
              </Field>
            </div>
            <Field label="現場住所"><input value={form.address} onChange={set("address")} className={inputCls} /></Field>
            <Field label="工事内容"><textarea rows={2} value={form.description} onChange={set("description")} className={`resize-y ${inputCls}`} /></Field>
            <div>
              <span className="font-mono text-[10px] tracking-[0.3em] text-gray-500">担当スタッフ（複数選択可）</span>
              <div className="mt-2 flex flex-wrap gap-x-5 gap-y-2">
                {staff.filter((s) => s.is_active).map((s) => (
                  <label key={s.id} className="flex items-center gap-2 text-sm text-gray-300">
                    <input data-testid={`project-form-staff-${s.id}`} type="checkbox" checked={form.staff_ids.includes(s.id)} onChange={() => toggleStaff(s.id)} className="h-4 w-4 accent-accent" />
                    {s.name}
                  </label>
                ))}
                {staff.length === 0 && <p className="text-xs text-gray-500">先に「スタッフ」メニューからスタッフを登録してください。</p>}
              </div>
            </div>
            <Field label="備考"><textarea rows={2} value={form.notes} onChange={set("notes")} className={`resize-y ${inputCls}`} /></Field>
            <button data-testid="project-form-submit" type="submit" disabled={saving} className="w-full bg-accent py-3 font-display text-sm font-bold tracking-[0.2em] text-ink transition-colors hover:bg-accent-hover disabled:opacity-50">
              {saving ? "保存中..." : "保存する"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
